// =============================================================================
// BP-07-03: Resources Frontend - API Client
// =============================================================================

import apiClient from '@/core/lib/api-client';
import type {
  Resource,
  ResourceCategory,
  Diagnosis,
  SavedResource,
  ResourceFilters,
  ResourceSearchParams,
  PaginationParams,
  PaginatedResponse,
  SaveResourceRequest,
  RateResourceRequest,
} from '@/features/resources/types/resourcesTypes';

// -----------------------------------------------------------------------------
// Resources
// -----------------------------------------------------------------------------

/**
 * Get paginated list of resources with optional filters
 */
export async function getResources(
  filters: ResourceFilters = {},
  pagination: PaginationParams = {}
): Promise<PaginatedResponse<Resource>> {
  const params = new URLSearchParams();
  
  // Add filters
  if (filters.categorySlug) params.append('category', filters.categorySlug);
  if (filters.diagnosisId) params.append('diagnosis_id', filters.diagnosisId);
  if (filters.diagnosisSlug) params.append('diagnosis', filters.diagnosisSlug);
  if (filters.resourceType) params.append('type', filters.resourceType);
  if (filters.lifeStage) params.append('life_stage', filters.lifeStage);
  if (filters.ageMin !== undefined) params.append('age_min', filters.ageMin.toString());
  if (filters.ageMax !== undefined) params.append('age_max', filters.ageMax.toString());
  if (filters.tags?.length) params.append('tags', filters.tags.join(','));
  if (filters.isPremium !== undefined) params.append('is_premium', filters.isPremium.toString());
  if (filters.search) params.append('search', filters.search);
  
  // Add pagination
  if (pagination.page) params.append('page', pagination.page.toString());
  if (pagination.limit) params.append('limit', pagination.limit.toString());
  if (pagination.sortBy) params.append('sort_by', pagination.sortBy);
  if (pagination.sortOrder) params.append('sort_order', pagination.sortOrder);

  const { data } = await apiClient.get<PaginatedResponse<Resource>>(
    `/resources?${params.toString()}`
  );
  return data;
}

/**
 * Get a single resource by slug
 */
export async function getResource(slug: string): Promise<Resource> {
  const { data } = await apiClient.get<Resource>(`/resources/${slug}`);
  return data;
}

/**
 * Get featured resources
 */
export async function getFeaturedResources(limit = 6): Promise<Resource[]> {
  const { data } = await apiClient.get<Resource[]>(`/resources/featured?limit=${limit}`);
  return data;
}

/**
 * Get popular resources (most viewed)
 */
export async function getPopularResources(limit = 10): Promise<Resource[]> {
  const { data } = await apiClient.get<Resource[]>(`/resources/popular?limit=${limit}`);
  return data;
}

/**
 * Search resources with full-text search
 */
export async function searchResources(
  params: ResourceSearchParams
): Promise<PaginatedResponse<Resource>> {
  const searchParams = new URLSearchParams();
  
  searchParams.append('q', params.q);
  if (params.categorySlug) searchParams.append('category', params.categorySlug);
  if (params.diagnosisSlug) searchParams.append('diagnosis', params.diagnosisSlug);
  if (params.resourceType) searchParams.append('type', params.resourceType);
  if (params.lifeStage) searchParams.append('life_stage', params.lifeStage);
  if (params.page) searchParams.append('page', params.page.toString());
  if (params.limit) searchParams.append('limit', params.limit.toString());

  const { data } = await apiClient.get<PaginatedResponse<Resource>>(
    `/resources/search?${searchParams.toString()}`
  );
  return data;
}

/**
 * Rate a resource (1-5 stars)
 */
export async function rateResource(
  resourceId: string,
  rating: RateResourceRequest
): Promise<{ avgRating: number; ratingCount: number }> {
  const { data } = await apiClient.post(`/resources/${resourceId}/rate`, rating);
  return data;
}

// -----------------------------------------------------------------------------
// Categories
// -----------------------------------------------------------------------------

/**
 * Get all resource categories (tree structure)
 */
export async function getResourceCategories(): Promise<ResourceCategory[]> {
  const { data } = await apiClient.get<{ items: ResourceCategory[]; total: number }>('/resources/categories');
  return data.items;
}

/**
 * Get resources by category slug
 */
export async function getResourcesByCategory(
  categorySlug: string,
  pagination: PaginationParams = {}
): Promise<PaginatedResponse<Resource>> {
  const params = new URLSearchParams();
  if (pagination.page) params.append('page', pagination.page.toString());
  if (pagination.limit) params.append('limit', pagination.limit.toString());
  if (pagination.sortBy) params.append('sort_by', pagination.sortBy);
  if (pagination.sortOrder) params.append('sort_order', pagination.sortOrder);

  const { data } = await apiClient.get<PaginatedResponse<Resource>>(
    `/resources/categories/${categorySlug}?${params.toString()}`
  );
  return data;
}

// -----------------------------------------------------------------------------
// Diagnoses
// -----------------------------------------------------------------------------

/**
 * Get all diagnoses
 */
export async function getDiagnoses(): Promise<Diagnosis[]> {
  const { data } = await apiClient.get<{ items: Diagnosis[]; total: number }>('/diagnoses');
  return data.items;
}

/**
 * Get a single diagnosis by slug
 */
export async function getDiagnosis(slug: string): Promise<Diagnosis> {
  const { data } = await apiClient.get<Diagnosis>(`/diagnoses/${slug}`);
  return data;
}

/**
 * Get resources for a specific diagnosis
 */
export async function getDiagnosisResources(
  diagnosisSlug: string,
  pagination: PaginationParams = {}
): Promise<PaginatedResponse<Resource>> {
  const params = new URLSearchParams();
  if (pagination.page) params.append('page', pagination.page.toString());
  if (pagination.limit) params.append('limit', pagination.limit.toString());
  if (pagination.sortBy) params.append('sort_by', pagination.sortBy);
  if (pagination.sortOrder) params.append('sort_order', pagination.sortOrder);

  const { data } = await apiClient.get<PaginatedResponse<Resource>>(
    `/diagnoses/${diagnosisSlug}/resources?${params.toString()}`
  );
  return data;
}

/**
 * Get related diagnoses for a diagnosis
 */
export async function getRelatedDiagnoses(
  diagnosisSlug: string
): Promise<Diagnosis[]> {
  const { data } = await apiClient.get<Diagnosis[]>(
    `/diagnoses/${diagnosisSlug}/related`
  );
  return data;
}

/**
 * Get recommended specialists for a diagnosis
 */
export async function getDiagnosisSpecialists(
  diagnosisSlug: string
): Promise<string[]> {
  const { data } = await apiClient.get<string[]>(
    `/diagnoses/${diagnosisSlug}/specialists`
  );
  return data;
}

/**
 * Search diagnoses
 */
export async function searchDiagnoses(query: string): Promise<Diagnosis[]> {
  const { data } = await apiClient.get<Diagnosis[]>(
    `/diagnoses/search?q=${encodeURIComponent(query)}`
  );
  return data;
}

// -----------------------------------------------------------------------------
// Saved Resources
// -----------------------------------------------------------------------------

/**
 * Get user's saved resources
 */
export async function getSavedResources(
  pagination: PaginationParams = {}
): Promise<PaginatedResponse<SavedResource>> {
  const params = new URLSearchParams();
  if (pagination.page) params.append('page', pagination.page.toString());
  if (pagination.limit) params.append('limit', pagination.limit.toString());

  const { data } = await apiClient.get<PaginatedResponse<SavedResource>>(
    `/users/me/saved-resources?${params.toString()}`
  );
  return data;
}

/**
 * Save a resource
 */
export async function saveResource(
  resourceId: string,
  request: SaveResourceRequest = {}
): Promise<SavedResource> {
  const { data } = await apiClient.post<SavedResource>(
    `/resources/${resourceId}/save`,
    request
  );
  return data;
}

/**
 * Unsave a resource
 */
export async function unsaveResource(resourceId: string): Promise<void> {
  await apiClient.delete(`/resources/${resourceId}/save`);
}

/**
 * Update saved resource notes/folder
 */
export async function updateSavedResource(
  savedResourceId: string,
  request: SaveResourceRequest
): Promise<SavedResource> {
  const { data } = await apiClient.patch<SavedResource>(
    `/saved-resources/${savedResourceId}`,
    request
  );
  return data;
}

/**
 * Get saved resource folders
 */
export async function getSavedResourceFolders(): Promise<string[]> {
  const { data } = await apiClient.get<string[]>('/users/me/saved-resources/folders');
  return data;
}

// -----------------------------------------------------------------------------
// Export all functions
// -----------------------------------------------------------------------------

export const resourcesApi = {
  // Resources
  getResources,
  getResource,
  getFeaturedResources,
  getPopularResources,
  searchResources,
  rateResource,
  
  // Categories
  getResourceCategories,
  getResourcesByCategory,
  
  // Diagnoses
  getDiagnoses,
  getDiagnosis,
  getDiagnosisResources,
  getRelatedDiagnoses,
  getDiagnosisSpecialists,
  searchDiagnoses,
  
  // Saved
  getSavedResources,
  saveResource,
  unsaveResource,
  updateSavedResource,
  getSavedResourceFolders,
};

